// Meet the Companions — four-card intro to Otto (Manager), Summi (History),
// Quill (Writing), Sage (Research). Sits between Hero and HigherIntelligence.
import { Mascot, type MascotName } from "../components/Mascot";

type Companion = {
  name: MascotName;
  role: string;
  accent: string;
  tint: string;
  body: string;
};

const COMPANIONS: Companion[] = [
  {
    name: "Otto",
    role: "The Manager",
    accent: "#6366F1",
    tint: "#E0E7FF",
    body: "Runs the team. Knows which Companion to call for every task.",
  },
  {
    name: "Summi",
    role: "History",
    accent: "#EC4899",
    tint: "#FCE7F3",
    body: "Knows your patients. Ask about their medical history and what was tried before.",
  },
  {
    name: "Quill",
    role: "Writing",
    accent: "#F59E0B",
    tint: "#FEF3C7",
    body: "Edits notes, drafts client emails, builds templates — all in your voice.",
  },
  {
    name: "Sage",
    role: "Research",
    accent: "#10B981",
    tint: "#D1FAE5",
    body: "Searches reputable veterinary sources. Every answer is sourced.",
  },
];

export function MeetTheCompanions() {
  return (
    <section className="border-t border-grindigo-100 bg-white px-[clamp(24px,5vw,80px)] py-[72px]">
      <div className="mx-auto max-w-[1240px]">
        <div className="mb-10 text-center">
          <div className="mb-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-indigo-700">
            The team
          </div>
          <h2 className="font-companion tracking-[-0.01em] mx-auto mt-0 max-w-[620px] text-[clamp(28px,3.4vw,40px)] font-bold tracking-[-0.02em] text-primary [text-wrap:balance]">
            Meet the Companions.
          </h2>
        </div>

        <div className="meet-grid grid grid-cols-4 gap-5">
          {COMPANIONS.map((c) => (
            <div
              key={c.name}
              style={
                {
                  "--accent": c.accent,
                  "--tint": c.tint,
                } as React.CSSProperties
              }
              className="meet-card relative flex flex-col items-center overflow-hidden rounded-[20px] border border-grindigo-100 bg-white p-6 text-center shadow-[0_12px_30px_-18px_rgba(99,102,241,0.2)] transition-[transform,box-shadow,border-color] duration-300 [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)]"
            >
              {/* tint glow */}
              <div className="pointer-events-none absolute -top-[50px] left-1/2 h-[200px] w-[200px] -translate-x-1/2 rounded-full opacity-80 bg-[radial-gradient(circle,var(--tint)_0%,transparent_70%)]" />

              {/* avatar */}
              <div className="relative z-[1] mb-4 h-[88px] w-[88px] overflow-hidden rounded-full shadow-[0_6px_18px_-6px_rgba(99,102,241,0.35)]">
                <Mascot name={c.name} size={88} />
              </div>

              <div className="relative z-[1] mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--accent)]">
                {c.role}
              </div>
              <h3 className="font-companion tracking-[-0.01em] relative z-[1] mt-0 mb-2.5 text-[22px] font-bold leading-[1.1] tracking-[-0.02em] text-primary">
                {c.name}
              </h3>
              <p className="relative z-[1] m-0 text-[13px] leading-[1.5] text-secondary [text-wrap:pretty]">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
